import {
  CSSProperties,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import "./style.css";
import { Icons } from "../../utils/iconPath";
import "../../style/inputs.css";
import { CommonInputProps } from "../../type";
import CheckBox from "common/Checkbox";

interface DropdownProps extends CommonInputProps {
  dropDownData: any;
  selectedValue?: any;
  setSelectedValue: (value: any) => void;
  displayKey: string;
  valueKey: string;
  isAction?: boolean;
  isSearch?: boolean;
  isMultiple?: boolean;
  bottomText?: string;
  onClickBottomText?: MouseEventHandler;
  customClassName?: string;
  style?: CSSProperties;
  styleMenuContainer?: CSSProperties;
  styleValueAndMenuListContainer?: CSSProperties;
  disabledRenderItem?: boolean;
  menuContainerClassName?: string;
  containerStyle?: CSSProperties;
  compareKey?: string;
}

export const Dropdown = ({
  // CommonInputProps
  label,
  subLabel,
  optional,
  containerClassName,
  placeholder = "Select",
  errorMessage,
  disabled,

  // New Props
  containerStyle,
  customClassName,
  dropDownData,
  setSelectedValue,
  selectedValue,
  displayKey,
  valueKey,
  isMultiple = false,
  bottomText,
  onClickBottomText,
  isSearch = true,
  styleMenuContainer,
  menuContainerClassName,
  styleValueAndMenuListContainer,
  disabledRenderItem,
  compareKey,
}: DropdownProps) => {
  //  Hooks
  const menuRef = useRef<HTMLUListElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Local States
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string>("");
  const [searchItem, setSearchItem] = useState("");
  const [filteredData, setFilteredData] = useState<any[]>([]);

  // Variables
  const Zero = 0;
  const selectFullItem = valueKey === "all";

  // Functions
  const selectedValueHandler = (value: any) => {
    if (isMultiple) {
      toggleItemSelection(value);
    } else {
      setSelectedValue(value);
      setIsOpen(false);
    }
  };
  const toggleItemSelection = (itemValue: any) => {
    const selectedIndex = selectedValue.indexOf(itemValue);
    let updatedSelection: any[] = [...selectedValue];
    if (selectedIndex !== -1) {
      updatedSelection.splice(selectedIndex, 1);
    } else {
      updatedSelection.push(itemValue);
    }
    setSelectedValue(updatedSelection);
  };
  const handleClick = () => {
    if (disabled) {
      setIsOpen(false);
      return;
    }
    setIsOpen(!isOpen);
  };
  const handleScroll = () => {
    if (menuRef.current && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      if (window.innerHeight - containerRect.bottom < menuRect.height - 10) {
        menuRef.current.style.bottom = "100%";
        menuRef.current.style.top = "auto";
        menuRef.current.style.transition = "0.0s";
      } else {
        menuRef.current.style.bottom = "auto";
        menuRef.current.style.top = "auto";
        menuRef.current.style.transition = "0.0s";
      }
    }
  };
  const findMatchingValues = (
    arrayOfObjects: any[],
    singleArray: any[],
    valueKey: string
  ) => {
    if (Array?.isArray(arrayOfObjects) && Array?.isArray(singleArray)) {
      return arrayOfObjects?.filter((obj: any) =>
        singleArray?.includes(selectFullItem ? obj : obj[valueKey])
      );
    } else {
      console.error("arrayOfObjects and singleArray must both be arrays.");
      return [];
    }
  };

  const removeSelectedItem = (id: number) => {
    const indexToRemove = selectedValue?.indexOf(id);
    if (selectedValue && isMultiple) {
      let copyArray = [...selectedValue];
      copyArray.splice(indexToRemove, 1);
      setSelectedValue(copyArray);
    }
  };

  // Effects
  useEffect(() => {
    if (dropDownData && isSearch && displayKey) {
      let updateArray = [...dropDownData];
      const filteredData = updateArray?.filter((item: any) => {
        return String(item[displayKey])
          ?.toLowerCase()
          ?.includes(searchItem?.toLowerCase());
      });
      setFilteredData(filteredData);
    }
  }, [dropDownData, searchItem, isSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    handleScroll();
  }, [isOpen]);

  useEffect(() => {
    if (isMultiple) setSelectedValue(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    if (dropDownData) {
      const foundKey = selectFullItem ? compareKey : valueKey;
      const found = dropDownData?.find((element: Record<string, any>) => {
        return element?.[foundKey as string] === selectedValue;
      });

      setDisplayName(found?.[displayKey]);
    }
  }, [dropDownData, selectedValue, compareKey]);

  useEffect(() => {
    if (isOpen) {
      const button = document.getElementById("customButton");
      if (button) button.style.position = "unset";
    }
  }, [isOpen]);

  return (
    <div
      className={`DROPDOWN CustomDropdown ${
        disabled ? "DISABLED" : ""
      } ${containerClassName} `}
      style={containerStyle}
    >
      {/* Label and Optional Tag */}
      {label && (
        <div className="LABEL_AND_OPTIONAL_TAG_CONTAINER">
          <label className="LABEL textOverFlowDots">{label}</label>
          {optional && <span className="OPTIONAL_TAG">{"optional"}</span>}
        </div>
      )}

      {/* Sub Label */}
      {subLabel && <p className="SUB_LABEL description ">{subLabel}</p>}

      {/* Field */}
      <div
        style={{
          position: "relative",
          ...styleValueAndMenuListContainer,
        }}
        ref={containerRef}
      >
        <button
          type="button"
          className={`DROPDOWN_FIELD_AND_CHILDREN dropdownFieldAndChildren ${customClassName} ${
            errorMessage ? "ERROR_BORDER" : ""
          }`}
          onClick={handleClick}
          disabled={disabled}
        >
          <p className="DROPDOWN_VALUE field">
            {isMultiple && selectedValue?.length > 0
              ? `${selectedValue?.length} ${
                  selectedValue?.length > 1 ? "items" : "item"
                } selected`
              : displayName || placeholder}
          </p>
          <img
            src={Icons.DROP_DOWN_ICON}
            alt="arrowDown"
            className={`arrowDown ${isOpen ? "arrowDownActive" : ""}`}
          />
        </button>

        {/* Error */}
        {errorMessage && <p className="ERROR_TEXT">{errorMessage}</p>}

        {/* Dropdown Menu */}
        {isOpen ? (
          <ul
            className={`dropdownMenuContainer ${menuContainerClassName}`}
            ref={menuRef}
            style={{ ...styleMenuContainer }}
          >
            {isSearch && (
              <li>
                <input
                  type="search"
                  placeholder="Search"
                  value={searchItem}
                  onChange={(e) => setSearchItem(e.target.value)}
                  className="dropdownItemSearchBox"
                />
              </li>
            )}
            {(isSearch ? filteredData : dropDownData)?.length <= Zero ? (
              <li className="bodyText noDataFound">No Data Found</li>
            ) : (
              <>
                {(isSearch ? filteredData : dropDownData)?.map(
                  (item: Record<string, any>, index: number) => {
                    const isSelected = selectFullItem
                      ? selectedValue === item[compareKey as string]
                      : selectedValue === item[valueKey] && !isMultiple;

                    const dropdownClass = isSelected
                      ? "dropdownElementActive bodyText"
                      : "dropdownElement bodyText";

                    const isDisabled = disabledRenderItem
                      ? selectedValue?.includes(
                          selectFullItem ? item : item[valueKey]
                        )
                      : false;

                    return (
                      <li
                        onClick={(e) => {
                          if (isMultiple) {
                            e.stopPropagation();
                            return;
                          } else {
                            selectedValueHandler(
                              selectFullItem ? item : item[valueKey]
                            );
                            setDisplayName(item[displayKey]);
                          }
                        }}
                        key={index}
                        className={`dropdownItem unselectable ${dropdownClass}`}
                      >
                        {isMultiple && (
                          <CheckBox
                            value={selectedValue?.includes(
                              selectFullItem ? item : item[valueKey]
                            )}
                            onChange={() => {
                              toggleItemSelection(
                                selectFullItem ? item : item[valueKey]
                              );
                            }}
                            label={item[displayKey]}
                            className="dropdownItemWithCheckBox"
                            style={undefined}
                            name={undefined}
                            id={undefined}
                            disabled={undefined}
                            required={undefined}
                          />
                        )}

                        {!isMultiple ? item[displayKey] : null}
                      </li>
                    );
                  }
                )}
              </>
            )}
          </ul>
        ) : null}
      </div>

      {/* Selected Chips */}
      {dropDownData &&
      selectedValue &&
      isMultiple &&
      findMatchingValues(dropDownData, selectedValue, valueKey)?.length > 0 ? (
        <div className="dropdownSelectedItemRender">
          {findMatchingValues(dropDownData, selectedValue, valueKey)?.map(
            (item: Record<string, any>, index: number) => {
              return (
                <SelectedItemChip
                  item={item?.[displayKey]}
                  key={index}
                  onClick={() => {
                    removeSelectedItem(item?.[valueKey]);
                  }}
                />
              );
            }
          )}
        </div>
      ) : null}

      {/* Bottom Text */}
      {bottomText ? (
        <div className="bottomText unselectable" onClick={onClickBottomText}>
          {bottomText}
        </div>
      ) : null}
    </div>
  );
};

const SelectedItemChip = ({
  item,
  onClick,
}: {
  item: string;
  onClick?: MouseEventHandler;
}) => {
  return (
    <div className="selectedItemChip">
      <span className="selectedItemName">{item}</span>
      <img
        src={Icons.CLOSE_ICON}
        alt="close icon"
        className="selectedItemChipCloseIcon"
        onClick={onClick}
      />
    </div>
  );
};
